import {ReactNode, useCallback, useMemo, useState} from "react";
import {useAuthenticatedUser} from "@/auth/AuthHooks.ts";
import {useCampaign} from "@/campaigns/CampaignContextHooks.ts";
import {
    ActiveEncounter,
    ActiveEncounterContext,
    ActiveEncounterContextValue,
} from "@/encounter/ActiveEncounterHooks.ts";

function storageKey(username: string, campaignId: string) {
    return `diredawn:active-encounter:${encodeURIComponent(username)}:${campaignId}`;
}

function readStoredEncounter(key: string, campaignId: string): ActiveEncounter | undefined {
    try {
        const stored = localStorage.getItem(key);
        if (!stored) return undefined;
        const candidate = JSON.parse(stored) as Partial<ActiveEncounter>;
        if (candidate.campaignId !== campaignId || typeof candidate.id !== "string" || typeof candidate.name !== "string") {
            localStorage.removeItem(key);
            return undefined;
        }
        return {id: candidate.id, name: candidate.name, campaignId: candidate.campaignId};
    } catch {
        return undefined;
    }
}

export function ActiveEncounterProvider({children}: { children: ReactNode }) {
    const {campaign} = useCampaign();
    const {username} = useAuthenticatedUser();
    const key = storageKey(username, campaign.id);
    const [activeEncounter, setStoredActiveEncounter] = useState<ActiveEncounter | undefined>(
        () => readStoredEncounter(key, campaign.id),
    );

    const setActiveEncounter = useCallback((encounter: ActiveEncounter | undefined) => {
        setStoredActiveEncounter(encounter);
        try {
            if (encounter) localStorage.setItem(key, JSON.stringify(encounter));
            else localStorage.removeItem(key);
        } catch {
            // The in-memory selection still works when browser storage is unavailable.
        }
    }, [key]);

    const value = useMemo<ActiveEncounterContextValue>(() => ({
        activeEncounter,
        setActiveEncounter,
    }), [activeEncounter, setActiveEncounter]);

    return <ActiveEncounterContext.Provider value={value}>
        {children}
    </ActiveEncounterContext.Provider>;
}
